import { GridCellProps } from '@progress/kendo-react-grid';
import { SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import Switch from 'src/components/design-system/Switch';
import { round } from 'src/utils';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const IsTaxable: FC<GridCellProps> = (props) => {
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const { dataItem, field, onChange, dataIndex } = props;
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const handleChange = useCallback(
    (e: SwitchChangeEvent) => {
      onChange?.({
        dataIndex: dataIndex,
        dataItem: dataItem,
        field: field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value,
      });
    },
    [dataIndex, dataItem, field, onChange]
  );

  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item?.item?.taxableIndicator?.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);
  const isEdited = useMemo(
    () =>
      matchingItem.some(
        (item) => !!item?.item?.taxableIndicator?.value !== !!dataItem?.[field || '']
      ),
    [dataItem, field, matchingItem]
  );

  const handleHover = useCallback(() => {
    handleHoverOcrField(
      'taxableIndicator',
      matchingItem.map((item) => Number(item.item?.itemSequenceNumber?.value) - 1)
    );
  }, [handleHoverOcrField, matchingItem]);

  const tooltip = dataItem?.[field || ''] ? 'true' : 'false';

  return (
    <GridCellBase
      {...props}
      className="text-center flex justify-center"
      style={{ textAlign: 'center' }}
      disableToolTip
    >
      <div className="flex flex-nowrap items-center justify-between gap-1">
        <div data-tooltip-id={'global-tooltip'} data-tooltip-html={tooltip}>
          {!isInvoiceEditDisabled ? (
            <Switch
              id="invoiceSwitch"
              value={!!dataItem?.[field || '']}
              onLabel={'YES'}
              offLabel={'NO'}
              onChange={handleChange}
            />
          ) : (
            <span>{dataItem?.[field || ''] ? 'Yes' : 'No'}</span>
          )}
        </div>
        {initialInvoice.ocrGenerated && ocrConfidence !== 0 && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default IsTaxable;
